import { CloseScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { CommonModule, DecimalPipe } from '@angular/common';
import { HttpClientJsonpModule, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';

import { CurrencyComponent } from '@component/currency/currency.component';
import { EditOrderComponent } from '@component/edit-order/edit-order.component';
import { IncrementalInputComponent } from '@component/incremental-input/incremental-input.component';
import { ModalMessageConfirmComponent } from '@component/modal-message-confirm/modal-message-confirm.component';
import { SpinnerComponent } from '@component/spinner/spinner.component';
import { NgbDatepickerModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { OrderWksStatusComponent } from './../business/order-wks/order-wks-status/order-wks-status.component';
import { PositionBalanceWksComponent } from './../business/position-balance-wks/position-balance-wks.component';
import { PositionDayWksActionsComponent } from './../business/position-day-wks/position-day-wks-actions/position-day-wks-actions.component';
import { WorksheetContextMenuComponent } from './../business/worksheet/worksheet-context-menu/worksheet-context-menu.component';
import { AddToolsComponent } from './../component/add-tools/add-tools.component';
import { CustomTooltipModule } from './../component/custom-tooltip/custom-tooltip.module';
import { VirtualKeyboardComponent } from './../component/eletronic-signature-modal/virtual-keyboard/virtual-keyboard.component';
import { EmptyResultComponent } from './../component/empty-result/empty-result.component';
import { AbbreviateNumberModule } from './abbreviate-number.module';
import { AutoSizeInputDirective } from './directives/autosize-input.directive';
import { DatepickerMaskDirective } from './directives/datepicker-mask.directive';
import { DisableWksChangeDirective } from './directives/disable-wks-change.directive';
import { OnResizeDirective } from './directives/on-resize.directive';
import { NumberPipe } from './pipes/number.pipe';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BookControllerComponent } from '@business/book/book.controller';
import { QuoteChartComponent } from '@business/quote-chart/quote-chart.component';
import { GridsterModule } from 'angular-gridster2';

import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { AssetSummaryPopUpComponent } from '@business/asset-summary-pop-up/asset-summary-pop-up.component';
import { AssetSummaryComponent } from '@business/asset-summary/asset-summary.component';
import { BalanceConsultComponent } from '@business/balance-consult/balance-consult.component';
import { BrokerAuthenticationComponent } from '@business/broker-authentication/broker-authentication.component';
import { CustodyConsultComponent } from '@business/custody-consult/custody-consult.component';
import { ModalNewsComponent } from '@business/modal-news/modal-news.component';
import { NewsCardComponent } from '@business/modal-news/news-card/news-card.component';
import { NewsDetailComponent } from '@business/modal-news/news-detail/news-detail.component';
import { NewsGridComponent } from '@business/modal-news/news-grid/news-grid.component';
import { ModalTradingViewComponent } from '@business/modal-trading-view/modal-trading-view.component';
import { OrdersHistoryDetailComponent } from '@business/orders-history/orders-history-detail/orders-history-detail.component';
import { OrdersHistoryComponent } from '@business/orders-history/orders-history.component';
import { QuickSearchComponent } from '@business/quick-search/quick-search.component';
import { ReconnectComponent } from '@business/reconnect/reconnect.component';
import { SupportChannelsComponent } from '@business/support-channels/support-channels.component';
import { TimesTradesComponent } from '@business/times-trades/times-trades.component';
import { TradingViewComponent } from '@business/trading-view/trading-view.component';
import { UpdatePasswordComponent } from '@business/update-password/update-password.component';
import { VolumeAtPriceComponent } from '@business/volume-at-price/volume-at-price.component';
import { WorksheetConfigModalComponent } from '@business/worksheet/worksheet-config-modal/worksheet-config-modal.component';
import { WorksheetComponent } from '@business/worksheet/worksheet.component';
import { AssetInputHeaderComponent } from '@component/asset-input-header/asset-input-header.component';
import { AutoCompleteComponent } from '@component/auto-complete/auto-complete.component';
import { BilletActionsComponent } from '@component/billet-actions/billet-actions.component';
import { BilletPositionComponent } from '@component/billet-position/billet-position.component';

import { BookAggregatedComponent } from '../component/book-aggregated/book-aggregated.component';
import { BookDetailComponent } from '../component/book-detail/book-detail.component';
import { NumberFormatPipe } from './pipes/number-format.pipe';

import { BookStaircaseComponent } from '@component/book-staircase/book-staircase.component';
import { CenteredModalComponent } from '@component/centered-modal/centered-modal.component';
import { ComponentBlankComponent } from '@component/component-blank/component-blank.component';
import { ComponentSelectorInputComponent } from '@component/component-selector-input/component-selector-input.component';
import { ComponentSelectorSelectComponent } from '@component/component-selector-select/component-selector-select.component';
import { CustomWorkspaceModalComponent } from '@component/custom-workspace-modal/custom-workspace-modal.component';
import { NgbdDatepickerRange } from '@component/datepicker-range/datepicker-range';
import { FullheightModalComponent } from '@component/fullheight-modal/fullheight-modal.component';
import { GridOrderComponent } from '@component/grid-order/grid-order.component';
import { IncrementalInputSmallComponent } from '@component/incremental-input-small/incremental-input-small.component';
import { MinResolutionModalComponent } from '@component/min-resolution-modal/min-resolution-modal.component';
import { NormalModalComponent } from '@component/normal-modal/normal-modal.component';
import { NotificationBarComponent } from '@component/notification-bar/notification-bar.component';
import { OrderChartComponent } from '@component/order-chart/order-chart.component';
import { QuickSearchAutoCompleteComponent } from '@component/quick-search-auto-complete/quick-search-auto-complete.component';
import { SendOrderConfirmModalComponent } from '@component/send-order-confirm-modal/send-order-confirm-modal.component';
import { SimultaneousStopOrderComponent } from '@component/simultaneous-stop-order/simultaneous-stop-order.component';
import { StopGainBuyOrderComponent } from '@component/stop-gain-buy-order/stop-gain-buy-order.component';
import { TradeChartComponent } from '@component/trade-chart/trade-chart.component';
import { UserSettingsPopUpComponent } from '@component/user-settings-pop-up/user-settings-pop-up.component';
import { UserSettingsComponent } from '@component/user-settings/user-settings.component';
import { WorkspaceConfModalComponent } from '@component/workspace-conf-modal/workspace-conf-modal.component';
import { YoutubeLiveComponent } from '@component/youtube-live/youtube-live.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CurrencyPipe } from '@shared/pipes/currency.pipe';
import { FilterPipe } from '@shared/pipes/filter.pipe';
import { NgbToDatePipe } from '@shared/pipes/ngbToDate.pipe';
import { AutoBlurDirective } from '@utils/autoFocus';
import { AgGridModule } from 'ag-grid-angular';
import { ChartModule } from 'angular-highcharts';
import { LottieAnimationViewModule } from 'ng-lottie';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgxMasonryModule } from 'ngx-masonry';
// import { NgxMatDrpModule } from 'ngx-mat-daterange-picker';
import { AssessorSearchClientComponent } from '../business/assessor-search-client/assessor-search-client.component';
import { AuthBrokerComponent } from '../business/auth-broker/auth-broker.component';
import { BilletQuickComponent } from '../business/billet-quick/billet-quick.component';
import { BilletRootComponent } from '../business/billet-root/billet-root.component';
import { BilletWksComponent } from '../business/billet-wks/billet-wks.component';
import { BrokerageNoteComponent } from '../business/brokerage-note/brokerage-note.component';
import { DepositComponent } from '../business/deposit/deposit.component';
import { DictionaryErrorComponent } from '../business/dictionary-error/dictionary-error.component';
import { EducationalComponent } from '../business/educational/educational.component';
import { ExtractFinancialComponent } from '../business/extract-financial/extract-financial.component';
import { HighLowDayComponent } from '../business/high-low-day/high-low-day.component';
import { ManualOnlineComponent } from '../business/manual-online/manual-online.component';
import { NewsWksComponent } from '../business/news-wks/news-wks.component';
import { OrderWksComponent } from '../business/order-wks/order-wks.component';
import { PositionDayWksComponent } from '../business/position-day-wks/position-day-wks.component';
import { RankingComponent } from '../business/ranking/ranking.component';
import { UpdatePasswordNegotiationComponent } from '../business/update-password-negotiation/update-password-negotiation.component';
import { UpdateSignatureComponent } from '../business/update-signature/update-signature.component';
import { AuctionHammerComponent } from '../component/auction-hammer/auction-hammer.component';
import { AutoCompleteAssessorComponent } from '../component/auto-complete-assessor/auto-complete-assessor.component';
import { BilletModalFloatComponent } from '../component/billet-modal-float/billet-modal-float.component';
import { BilletWksConfirmationComponent } from '../component/billet-wks-confirmation/billet-wks-confirmation.component';
import { BilletWksInputsComponent } from '../component/billet-wks-inputs/billet-wks-inputs.component';
import { ConfirmationModalComponent } from '../component/confirmation-modal/confirmation-modal.component';
import { EletronicSignatureModalComponent } from '../component/eletronic-signature-modal/eletronic-signature-modal.component';
import { ErrorScreenComponent } from '../component/error-screen/error-screen.component';
import { ModalAuthBrokerLoginComponent } from '../component/modal-auth-broker-login/modal-auth-broker-login.component';
import { ModalPasswordComponent } from '../component/modal-password/modal-password.component';
import { ModalSignatureComponent } from '../component/modal-signature/modal-signature.component';
import { SessionModalComponent } from '../component/session-modal/session-modal.component';
import { TermOperatorModalComponent } from '../component/term-operator-modal/term-operator-modal.component';
import { YoutubeLiveModalComponent } from '../component/youtube-live/youtube-live-modal/youtube-live-modal.component';
import { OrderWksQuickActionsComponent } from './../business/order-wks/order-wks-quick-actions/order-wks-quick-actions.component';
import { FilterDailyPositionsPipe } from './pipes/dailyPosition.pipe';
import { SafeUrlPipe } from './pipes/safe-url.pipe';

import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatMenuModule } from '@angular/material/menu';




import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';

import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyTabsModule } from '@angular/material/legacy-tabs';
import { MatListModule } from '@angular/material/list';

import { MatLegacyAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyOptionModule } from '@angular/material/legacy-core';
import { MatLegacyRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import player from 'lottie-web';
import { LottieModule } from 'ngx-lottie';
import {OpenDrawerModule} from "./directives/open-drawer.module";
import {MenuModule} from "./components/menu/menu.module";
import {MenuComponent} from "./components/menu/menu.component";
import {MatSidenavModule} from "@angular/material/sidenav";
import {AccountNegotiationModule} from "./components/account-negotiation/account-negotiation.module";
import { TradingViewModule } from './components/trading-view/trading-view.module';

export function scrollFactory(overlay: Overlay): () => CloseScrollStrategy {
  return () => overlay.scrollStrategies.close();
}

export function playerFactory() {
  return player;
}

const VERBOSE_DATE_FORMAT = {
  parse: {
    dateInput: ['LL', 'YYYY-MM-DD', 'DD/MM/YYYY'],
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const materialModules = [
  MatDialogModule,
  MatIconModule,
  NgbDatepickerModule,
  NgbTooltipModule,
  MatLegacyOptionModule,
  MatLegacyAutocompleteModule,
  // MatAutocompleteModule,
  MatCardModule,
  MatGridListModule,
  MatButtonToggleModule,
  MatMenuModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatExpansionModule,
  MatSlideToggleModule,
  MatListModule,
  MatSnackBarModule,
  MatProgressSpinnerModule,
  MatBottomSheetModule,
  MatLegacyButtonModule,
  MatLegacyCheckboxModule,
  MatLegacyInputModule,
  MatLegacyTabsModule,
  MatLegacyRadioModule,
  MatLegacySelectModule,
  MatLegacyTooltipModule,
  PdfViewerModule,
  // MatButtonModule,
  // MatSelectModule,
  // MatTabsModule,
  // MatInputModule,
  // MatCheckboxModule,
  // MatRadioModule,
  // MatTooltipModule,
  // ScrollDispatchModule,
];
@NgModule({
  imports: [
    ChartModule,
    CurrencyMaskModule,
    HttpClientModule,
    CommonModule,
    NgbModule,
    GridsterModule,
    // NgxMatDrpModule,
    RouterModule,
    FormsModule,
    HttpClientJsonpModule,
    ReactiveFormsModule,
    AgGridModule.withComponents([
      CurrencyComponent
    ]),
    materialModules,
    LottieAnimationViewModule.forRoot(),
    LottieModule.forRoot({player: playerFactory}),
    NgxMasonryModule,
    CurrencyMaskModule,
    CustomTooltipModule,
    AbbreviateNumberModule,
    MatSidenavModule,
    AccountNegotiationModule,
    TradingViewModule
  ],
  declarations: [
    IncrementalInputComponent,
    AssetSummaryComponent,
    AssetSummaryPopUpComponent,
    UserSettingsComponent,
    UserSettingsPopUpComponent,
    QuickSearchComponent,
    SpinnerComponent,
    CurrencyComponent,
    ModalMessageConfirmComponent,
    ReconnectComponent,
    BookControllerComponent,
    AutoCompleteComponent,
    QuickSearchAutoCompleteComponent,
    QuoteChartComponent,
    TradingViewComponent,
    TimesTradesComponent,
    WorksheetComponent,
    WorkspaceConfModalComponent,
    FullheightModalComponent,
    NormalModalComponent,
    CenteredModalComponent,
    FilterPipe,
    FilterDailyPositionsPipe,
    NgbToDatePipe,
    VolumeAtPriceComponent,
    UpdatePasswordComponent,
    UpdatePasswordNegotiationComponent,
    BrokerAuthenticationComponent,
    SimultaneousStopOrderComponent,
    OrdersHistoryComponent,
    EditOrderComponent,
    OrdersHistoryDetailComponent,
    WorksheetConfigModalComponent,
    WorksheetContextMenuComponent,
    ModalTradingViewComponent,
    ModalNewsComponent,
    NewsCardComponent,
    CustodyConsultComponent,
    NewsGridComponent,
    NewsDetailComponent,
    BalanceConsultComponent,
    CurrencyPipe,
    NumberPipe,
    NumberFormatPipe,
    NgbdDatepickerRange,
    SendOrderConfirmModalComponent,
    CustomWorkspaceModalComponent,
    ComponentSelectorInputComponent,
    ComponentBlankComponent,
    ComponentSelectorSelectComponent,
    StopGainBuyOrderComponent,
    MinResolutionModalComponent,
    ErrorScreenComponent,
    YoutubeLiveComponent,
    SafeUrlPipe,
    SessionModalComponent,
    YoutubeLiveModalComponent,
    AutoBlurDirective,
    ModalPasswordComponent,
    AuthBrokerComponent,
    AutoSizeInputDirective,
    DatepickerMaskDirective,
    DisableWksChangeDirective,
    UpdateSignatureComponent,
    ModalSignatureComponent,
    AssessorSearchClientComponent,
    AutoCompleteAssessorComponent,
    ExtractFinancialComponent,
    BrokerageNoteComponent,
    RankingComponent,
    EducationalComponent,
    ManualOnlineComponent,
    ModalAuthBrokerLoginComponent,
    BilletQuickComponent,
    BilletWksComponent,
    NewsWksComponent,
    HighLowDayComponent,
    PositionDayWksComponent,
    PositionBalanceWksComponent,
    OrderWksComponent,
    OrderWksQuickActionsComponent,
    OrderWksStatusComponent,
    IncrementalInputSmallComponent,
    BilletWksInputsComponent,
    PositionDayWksActionsComponent,
    ConfirmationModalComponent,
    BilletWksConfirmationComponent,
    AddToolsComponent,
    EletronicSignatureModalComponent,
    VirtualKeyboardComponent,
    EmptyResultComponent,
    TermOperatorModalComponent,
    DictionaryErrorComponent,
    DepositComponent,
    AuctionHammerComponent,
    BilletModalFloatComponent,
    AssetInputHeaderComponent,
    OnResizeDirective,
    BilletRootComponent,
    BilletPositionComponent,
    BilletActionsComponent,
    TradeChartComponent,
    OnResizeDirective,
    OrderChartComponent,
    GridOrderComponent,
    SupportChannelsComponent,
    BookDetailComponent,
    BookAggregatedComponent,
    BookStaircaseComponent,
    NotificationBarComponent
  ],
  entryComponents: [
    CustomWorkspaceModalComponent,
    AssetSummaryComponent,
    AssetSummaryPopUpComponent,
    UserSettingsComponent,
    UserSettingsPopUpComponent,
    TradingViewComponent,
    WorkspaceConfModalComponent,
    FullheightModalComponent,
    NormalModalComponent,
    ModalPasswordComponent,
    CenteredModalComponent,
    ModalMessageConfirmComponent,
    AutoCompleteComponent,
    EditOrderComponent,
    UpdatePasswordComponent,
    UpdatePasswordNegotiationComponent,
    UpdateSignatureComponent,
    WorksheetConfigModalComponent,
    WorksheetContextMenuComponent,
    ReconnectComponent,
    BrokerAuthenticationComponent,
    ModalTradingViewComponent,
    ModalNewsComponent,
    CustodyConsultComponent,
    BalanceConsultComponent,
    MinResolutionModalComponent,
    YoutubeLiveModalComponent,
    SessionModalComponent,
    AuthBrokerComponent,
    ModalSignatureComponent,
    AssessorSearchClientComponent,
    AutoCompleteAssessorComponent,
    ExtractFinancialComponent,
    BrokerageNoteComponent,
    RankingComponent,
    EducationalComponent,
    ManualOnlineComponent,
    ModalAuthBrokerLoginComponent,
    OrderWksQuickActionsComponent,
    OrderWksStatusComponent,
    NewsDetailComponent,
    PositionDayWksActionsComponent,
    ConfirmationModalComponent,
    AddToolsComponent,
    EletronicSignatureModalComponent,
    TermOperatorModalComponent,
    DepositComponent,
    AuctionHammerComponent,
    BilletModalFloatComponent,
    BilletRootComponent,
    SupportChannelsComponent,
    BookDetailComponent,
    BookAggregatedComponent,
    PositionBalanceWksComponent,
    NotificationBarComponent
  ],
  exports: [
    IncrementalInputComponent,
    AssetSummaryComponent,
    AssetSummaryPopUpComponent,
    UserSettingsComponent,
    UserSettingsPopUpComponent,
    QuickSearchComponent,
    SpinnerComponent,
    CurrencyComponent,
    ModalMessageConfirmComponent,
    ReconnectComponent,
    BookControllerComponent,
    AutoCompleteComponent,
    QuickSearchAutoCompleteComponent,
    materialModules,
    RouterModule,
    FormsModule,
    HttpClientJsonpModule,
    ReactiveFormsModule,
    QuoteChartComponent,
    GridsterModule,
    // // ScrollDispatchModule,
    TradingViewComponent,
    TimesTradesComponent,
    WorksheetComponent,
    WorkspaceConfModalComponent,
    FilterPipe,
    FilterDailyPositionsPipe,
    NgbToDatePipe,
    NumberFormatPipe,
    VolumeAtPriceComponent,
    UpdatePasswordComponent,
    UpdatePasswordNegotiationComponent,
    UpdateSignatureComponent,
    SimultaneousStopOrderComponent,
    OrdersHistoryComponent,
    OrdersHistoryDetailComponent,
    EditOrderComponent,
    WorksheetConfigModalComponent,
    WorksheetContextMenuComponent,
    ModalTradingViewComponent,
    ModalNewsComponent,
    NewsCardComponent,
    CustodyConsultComponent,
    NewsGridComponent,
    NewsDetailComponent,
    BalanceConsultComponent,
    SendOrderConfirmModalComponent,
    CustomWorkspaceModalComponent,
    ComponentSelectorInputComponent,
    ComponentBlankComponent,
    ComponentSelectorSelectComponent,
    StopGainBuyOrderComponent,
    CommonModule,
    NumberFormatPipe,
    MinResolutionModalComponent,
    CurrencyMaskModule,
    ErrorScreenComponent,
    YoutubeLiveComponent,
    AutoSizeInputDirective,
    DatepickerMaskDirective,
    DisableWksChangeDirective,
    AssessorSearchClientComponent,
    AutoCompleteAssessorComponent,
    ExtractFinancialComponent,
    BrokerageNoteComponent,
    RankingComponent,
    EducationalComponent,
    ManualOnlineComponent,
    ModalAuthBrokerLoginComponent,
    BilletQuickComponent,
    BilletWksComponent,
    NewsWksComponent,
    HighLowDayComponent,
    PositionDayWksComponent,
    PositionBalanceWksComponent,
    OrderWksComponent,
    OrderWksQuickActionsComponent,
    OrderWksStatusComponent,
    IncrementalInputSmallComponent,
    BilletWksInputsComponent,
    PositionDayWksActionsComponent,
    BilletWksConfirmationComponent,
    PdfViewerModule,
    AddToolsComponent,
    EmptyResultComponent,
    DepositComponent,
    AuctionHammerComponent,
    CustomTooltipModule,
    BilletModalFloatComponent,
    AssetInputHeaderComponent,
    BilletPositionComponent,
    BilletActionsComponent,
    TradeChartComponent,
    OnResizeDirective,
    BilletRootComponent,
    OrderChartComponent,
    GridOrderComponent,
    SupportChannelsComponent,
    BookDetailComponent,
    BookAggregatedComponent,
    BookStaircaseComponent,
    NotificationBarComponent
  ],
  providers: [
    DecimalPipe,
    { provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
    // { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_LEGACY_DATE_LOCALE_FACTORY] },
    // { provide: MAT_DATE_LOCALE_FACTORY, useClass: MomentDateAdapter, deps: [MAT_LEGACY_DATE_LOCALE_FACTORY] },
    // { provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, useClass: _MatLegacyOptionBase },
    // { provide: MAT_MOMENT_DATE_FORMATS, useValue: MY_DATE_FORMATS },


    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: VERBOSE_DATE_FORMAT },
    { provide: MAT_MOMENT_DATE_FORMATS, useValue: VERBOSE_DATE_FORMAT },
  ]
})
export class SharedModule { }

